import React, { useState, useEffect } from 'react';
import { Menu, X, Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navbar({ activeSection, scrollToSection }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'forside', label: 'Forside' },
    { id: 'ydelser', label: 'Jeg hjælper dig med' },
    { id: 'om-mig', label: 'Om mig' },
    { id: 'kontakt', label: 'Kontakt' }
  ];

  const handleNavClick = (id) => {
    setIsMobileMenuOpen(false);
    scrollToSection(id);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-[#F7F4F1] py-3 lg:py-4 xl:py-3 shadow-sm border-b border-[#EDE6DF]'
            : 'bg-transparent py-6 lg:py-8 xl:py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-row items-center justify-between lg:flex-col lg:items-start lg:gap-4 xl:flex-row xl:items-center xl:justify-between">
          {/* Logo & Mobile Menu Button Wrapper */}
          <div className="flex items-center justify-between w-full lg:w-auto">
            {/* Logo */}
            <button
              onClick={() => handleNavClick('forside')}
              className="flex flex-col items-start group text-left focus:outline-none"
            >
              <span className="font-sans-clean text-lg md:text-xl tracking-[0.15em] uppercase text-[#4A4A4A] font-semibold select-none transition-transform duration-300 group-hover:scale-[1.02] leading-none mb-1">
                Mette Brisson
              </span>
              <span className="font-sans-clean text-[9px] md:text-[10px] tracking-[0.3em] uppercase text-[#BFC8BE] font-semibold pl-0.5">
                Psykolog
              </span>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-[#4A4A4A] hover:text-[#BFC8BE] transition-colors focus:outline-none"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`font-sans-clean text-xs tracking-widest uppercase transition-all duration-300 relative py-2 focus:outline-none ${
                  activeSection === item.id
                    ? 'text-black font-semibold'
                    : 'text-[#2D2D2D]/80 hover:text-black'
                }`}
              >
                {item.label}
                {activeSection === item.id && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="absolute bottom-0 left-0 right-0 h-[1.5px] bg-[#BFC8BE]"
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </button>
            ))}
            <button
              onClick={() => handleNavClick('kontakt')}
              className="ml-4 px-5 py-2.5 bg-[#BFC8BE] hover:bg-[#BFC8BE]/90 text-white font-sans-clean text-xs tracking-widest uppercase rounded-sm transition-all duration-300 shadow-sm hover:shadow-md"
            >
              Book samtale
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#F7F4F1] pt-24 px-6 flex flex-col justify-between pb-12 lg:hidden"
          >
            <div className="flex flex-col gap-4 mt-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`text-left font-serif-elegant text-xl transition-colors py-1.5 focus:outline-none ${
                    activeSection === item.id
                      ? 'text-black font-semibold'
                      : 'text-[#2D2D2D]/80 hover:text-black'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-6 border-t border-[#EDE6DF] pt-8">
              <button
                onClick={() => handleNavClick('kontakt')}
                className="w-full py-4 bg-[#BFC8BE] text-white font-sans-clean text-sm tracking-widest uppercase rounded-sm text-center shadow-sm"
              >
                Book samtale
              </button>
              <div className="text-center text-xs text-[#4A4A4A]/60 font-sans-clean tracking-wider">
                Holger Danskes vej 3B, Frederiksberg · 27 89 30 29
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
